import { Button, Modal, ModalProps } from 'react-bootstrap';
import { createUseThemedStyles } from '@/hooks';
import { useState } from 'react';
import { Dash, Plus } from 'react-bootstrap-icons';

interface UseStylesProps {
	imageWidth?: number;
}

const useStyles = createUseThemedStyles((theme) => ({
	dialog: {
		width: '90vw',
		maxWidth: '90vw',
		height: '100%',
		maxHeight: '100%',
		margin: '0 auto',
		'& .modal-content': {
			maxHeight: '90vh',
		},
		'& .hackett-modal__body': {
			overflow: 'auto',
			position: 'relative',
			'& img': {
				height: 'auto',
				margin: '0 auto',
				display: 'block',
				width: ({ imageWidth }: UseStylesProps) => (imageWidth ? `${imageWidth}px` : 'auto'),
				transition: 'width 0.3s',
			},
		},
	},
	zoomControls: {
		top: 75,
		right: 8,
		zIndex: 1,
		display: 'flex',
		borderRadius: 4,
		overflow: 'hidden',
		alignItems: 'center',
		position: 'absolute',
		backgroundColor: theme.colors.white,
		border: `1px solid ${theme.colors.gray300}`,
		'& div': {
			padding: '0 16px',
		},
	},
}));

interface ImageModalProps extends ModalProps {
	imageSrc: string;
}

const MIN_ZOOM_LEVEL = 1.0;
const MAX_ZOOM_LEVEL = 3.0;

export const ImageModal = ({ imageSrc, ...props }: ImageModalProps) => {
	const [zoomLevel, setZoomLevel] = useState(1.0);
	const [naturalWidth, setNaturalWidth] = useState(0);
	const imageWidth = naturalWidth ? naturalWidth * zoomLevel : undefined;

	const classes = useStyles({ imageWidth });

	const handleEnter = () => {
		setZoomLevel(1.0);
	};

	const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
		setNaturalWidth(event.currentTarget.naturalWidth);
	};

	const handleZoomOutButtonClick = () => {
		setZoomLevel((curr) => {
			return Math.max(curr - 1, MIN_ZOOM_LEVEL);
		});
	};

	const handleZoomInButtonClick = () => {
		setZoomLevel((curr) => {
			return Math.min(curr + 1, MAX_ZOOM_LEVEL);
		});
	};

	return (
		<Modal dialogClassName={classes.dialog} centered onEntering={handleEnter} {...props}>
			<Modal.Header closeButton>
				<Modal.Title>View Image</Modal.Title>
				<div className={classes.zoomControls}>
					<Button
						variant="light"
						className="p-2"
						onClick={handleZoomOutButtonClick}
						disabled={zoomLevel <= MIN_ZOOM_LEVEL}
					>
						<Dash />
					</Button>
					<div>{zoomLevel}x</div>
					<Button
						variant="light"
						className="p-2"
						onClick={handleZoomInButtonClick}
						disabled={zoomLevel >= MAX_ZOOM_LEVEL}
					>
						<Plus />
					</Button>
				</div>
			</Modal.Header>
			<Modal.Body>
				<img src={imageSrc} alt="" onLoad={handleImageLoad} />
			</Modal.Body>
			<Modal.Footer>
				<div className="text-right">
					<Button type="button" className="mr-2" variant="outline-primary" onClick={props.onHide}>
						Close
					</Button>
				</div>
			</Modal.Footer>
		</Modal>
	);
};
